import faker from "faker";
import { Enrolment } from "../models/Enrolment";
import { PaginationModel } from "../models/Pagination";
import { CustomDetail, Person, PersonImage } from "../models/Person";
import { EnrolmentWrapper } from "../models/wrappers/EnrolmentWrapper";

import { Deserializer } from "jsonapi-serializer";

import createDebug from "debug";
import {
    INTERNAL_SERVER_ERROR,
    NO_CONTENT,
} from "../constants/response-status-codes";
import { Filter } from "../models/Filter";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { appendFilter } from "../utilities/filters";
import { APIConnector } from "./api/APIConnector";
import { createPagination } from "./utilities/paginationBuilder";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import {Translator} from "../contexts/translator";
import {TranslationKeys} from "../contexts/TranslationKeys";

const debug = createDebug("enrolment-service");

faker.seed(7);

export function sleepAsync(time = 500) {
    return new Promise<void>((resolve) => {
        setTimeout(() => {
            resolve();
        }, time);
    });
}

const apiConnector = new APIConnector();

export const storeMasterImage = async (masterImage) => {
    try {
        const image = {
            data: {
                id: masterImage.id || 0,
                type: "masterImage",
                attributes: {
                    newBase64ImageString: masterImage.imageString,
                },
                relationships: {
                    master: {
                        data: {
                            type: "master",
                            id: masterImage.masterId,
                        },
                    },
                    masterImageType: {
                        data: {
                            type: "masterImageType",
                            id: masterImage.masterImageType,
                        },
                    },
                },
            },
        };

        let response;

        const createNewImage = image.data.id === 0 ? true : false;
        const url = "masterImage" + (createNewImage ? "" : `/${image.data.id}`);
        await apiConnector
            .sendData(url, createNewImage, image)
            .then((data) => {
                response = data;
            })
            .catch((error) => {
                return new Promise((resolve, reject) => {
                    reject({ status: response.status, message: genericError });
                });
            });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createNewImage) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({
                            status: response.status,
                            message: getConstraintViolation(content.errors[0].detail),
                        });
                    }

                    new Deserializer().deserialize(
                        content,
                        (error, masterImage) => {
                            if (error) {
                                return reject({
                                    status: response.status,
                                    id: masterImage.id,
                                    success: false,
                                    message: genericError,
                                });
                            }
                            return resolve({
                                id: masterImage.id,
                                success: true,
                            });
                        }
                    );

                    return;
                }

                if (response.status === 204) {
                    return resolve({ id: masterImage.id, success: true });
                }

                const json = await response.json();
                return reject({
                    status: response.status,
                    id: masterImage.id,
                    success: false,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }

            return reject({
                status: response.status,
                id: masterImage.id,
                success: false,
                message: genericError,
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

const buildPersonImages = (images?) => {
    let masterImages: PersonImage[] = [];
    if (images && images.length > 0) {
        images.map((image) => {
            masterImages.push({
                base64Image: image["base64-image"],
                base64Thumb: image["base64-thumb"],
                id: image["id"],
                masterTypeId: parseInt(image["master-image-type"]["id"]),
            });
        });
    }

    return masterImages;
};

export async function getPersonByID(translate:Translator, id): Promise<Person> {
    try {
        let includes = "include=tags,masterType,masterDetails,profile,company,department,";
        includes += "masterDetails.masterDetailType.settingType,masterGroups.accessGroup,";
        includes += "masterImages.masterImageType,site";
        const masterImageFields = "fields[masterImage]=base64Thumb,masterImageType";
        let masterFields = "fields[master]=masterDetails,mstFirstname,mstDisplayname,mstLastname,";
        masterFields += "mstMiddlename,mstIdnumber,profile,department,company,mstStartDate,";
        masterFields += "mstPin,mstEmailAddress,mstMobileAddress,mstNoapb,mstDescription,";
        masterFields += "mstTitle,mstSuffix,mstGender,masterType,mstCurrent,totalTags,";
        masterFields += "totalCredentials,totalAccessGroups,totalTransacksForToday,totalFloors,totalMasterExpiries,totalOssCredentials,";
        masterFields += "totalAssets,masterImages,mstMobileAccessIdentifier,site";
        const masterDetailsFields = "fields[masterDetail]=masterDetailType,mdValBinary,mdValString";
        const masterDetailTypeFields = "fields[masterDetailType]=settingType";
        const settingTypeFields = "fields[settingType]=stSysname";
        const profileFields = "fields[profile]=trnName";
        const departmentFields = "fields[department]=deptName";
        const companyFields = "fields[company]=compName";
        const siteFields = "fields[site]=siteName";
        const masterImagesFields = "fields[masterImages]=base64Image";
        let url = `master/${id}?${includes}&${masterImageFields}&${masterFields}&`;
        url +=`${masterDetailsFields}&${masterDetailTypeFields}&${settingTypeFields}&`;
        url += `${profileFields}&${departmentFields}&${companyFields}&${siteFields}&`;
        url += `${masterImagesFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }

            new Deserializer().deserialize(json, (err: any, apiMaster: any) => {
                if (err) {
                    return reject({
                        status: response.status,
                        message: genericError,
                    });
                }

                const newMaster = buildEnrolment(apiMaster, "", translate);
                resolve(newMaster);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAccessProfileAndGroupCounts = async (masterId: number): Promise<{ accessProfileCount: number, accessGroupCount: number, floorGroupCount: number, ossGroupCount: number}> => {
    try {
        const masterFields = 'fields[master]=totalAccessGroups,totalFloorGroups,totalOssGroups,totalAccessProfiles';
        const url = `master/${masterId}?${masterFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }

            new Deserializer().deserialize(json, (err: any, data: any) => {
                if (err) {
                    return reject({
                        status: response.status,
                        message: genericError,
                    });
                }

                const counts = {
                    accessProfileCount: data['total-access-profiles'],
                    accessGroupCount: data['total-access-groups'],
                    floorGroupCount: data['total-floor-groups'],
                    ossGroupCount: data['total-oss-groups'],
                };
                resolve(counts);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const buildEnrolment = (m, birthdayDetails, translate:Translator) => {
    const customFields = m["master-details"] && m["master-details"].reduce(
        (enrolmentFragment, masterDetail, index) => {
            if (
                masterDetail["master-detail-type"] &&
                masterDetail["master-detail-type"]["setting-type"] &&
                masterDetail["master-detail-type"]["setting-type"]["st-sysname"]
            ) {
                enrolmentFragment[
                    masterDetail["master-detail-type"]["setting-type"][
                        "st-sysname"
                    ]
                ] = {
                    id: masterDetail["id"],
                    detailTypeId: masterDetail["master-detail-type"]["id"],
                    value: masterDetail["md-val-binary"]
                        ? masterDetail["md-val-binary"]
                        : masterDetail["md-val-string"]
                        ? masterDetail["md-val-string"]
                        : "",
                    isBoolean: masterDetail["md-val-binary"] ? true : false,
                };
            }
            return enrolmentFragment;
        },
        {}
    );

    const yes = translate.byKey(TranslationKeys.yes) ?? "Yes";
    const no = translate.byKey(TranslationKeys.no) ?? "No";

    return {
        custom: { ...customFields },
        id: m["id"],
        name: m["mst-displayname"],
        firstName: m["mst-firstname"],
        lastName: m["mst-lastname"],
        middleName: m["mst-middlename"],
        uid: m["mst-idnumber"],
        category: m.profile == null ? null : m.profile["trn-name"],
        department: m.department ? m.department["id"] : "",
        deptName: m.department ? m.department["dept-name"] : "",
        company: m.company ? m.company["id"] : "",
        companyName: m.company ? m.company["comp-name"] : "",
        dateStarted: m["mst-start-date"] ? new Date(m["mst-start-date"]) : undefined,
        workNumber: m["master-details"] ? "" : "",
        pin: m["mst-pin"] === 0 ? "" : m["mst-pin"],
        email: m["mst-email-address"] ? m["mst-email-address"] : "",
        mobileNumber: m["mst-mobile-address"] ? m["mst-mobile-address"] : "",
        birthday: birthdayDetails ? new Date(birthdayDetails["md-val-string"]) : undefined,
        birthdayId: birthdayDetails ? birthdayDetails["id"] : "",
        apbOverride: m["mst-noapb"],
        description: m["mst-description"],
        profileName: m.profile == null ? null : m.profile["trn-name"],
        profileId: m.profile == null ? 0 : m.profile["id"],
        images: m["master-images"] && m["master-images"].filter((masterImage) => !!masterImage).map((masterImage) => masterImage["base64-thumb"]) || [],
        selectedImage: 1,
        title: m["mst-title"],
        suffix: m["mst-suffix"],
        gender: m["mst-gender"], // Do not interpret here, just pass the value
        genderLabel: getGenderLabel(m["mst-gender"]),
        printTemplate: m.card ? m.card["id"] : "",
        timeAndAttendance: m["master-type"] ? m["master-type"]["id"] == 2 : false,
        accessType: getAccessType(m["master-type"]),
        enableCredentials: m["mst-current"],
        enableCredentialLabel:m["mst-current"] && m["mst-current"] === 1 ? yes : no,
        totalTags: m["total-tags"],
        totalCredentials: m["total-credentials"],
        totalAccessGroups: m["total-access-groups"],
        totalTransacksForToday: m["total-transacks-for-today"],
        totalFloors: m["total-floors"],
        totalAssets: m["total-assets"],
        masterImages: buildPersonImages(m["master-images"]),
        mstMobileAccessIdentifier: m["mst-mobile-access-identifier"],
        siteId: m["site"]?.["id"],
        site: m["site"]?.["site-name"],
        masterType:m['master-type']['trn-name'] || "",
        totalOssTags: m['total-oss-credentials'],
        totalMasterExpiries: m['total-master-expiries']
    };
};

export async function getEnrolmentNames(
    listParameters?: ListQuery
): Promise<{ rows: Name[]; pagination: PaginationModel }> {
    try {
        const filter: string = getFilterClause(
            listParameters?.filters,
            listParameters?.searchTerm
        );
        const partialURL = createPartialURL(
            listParameters?.goToPage,
            listParameters?.pageSize,
            listParameters?.sortBy,
            filter
        );

        const masterFields = `fields[master]=mstDisplayname,site`;
        const siteFields = `fields[site]=siteName`;
        const url = `master?${partialURL.startsWith("&") ? partialURL.substring(1) : partialURL}&include=site&${masterFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }

            new Deserializer().deserialize(
                json,
                (err: any, apiEnrolments: any[]) => {
                    if (err) {
                        return reject({
                            status: response.status,
                            message: genericError,
                        });
                    }

                    const enrolments: Name[] = apiEnrolments.map(
                        (enrolment) => {
                            return {
                                label: enrolment["mst-displayname"],
                                value: enrolment["id"],
                                optionalColumns: [
                                    {
                                        key: "site",
                                        value: enrolment["site"]?.["site-name"],
                                    },
                                ],
                            };
                        }
                    );

                    return resolve({
                        rows: enrolments,
                        pagination: newPagination,
                    });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export async function getEnrolmentNamesWithPagination(listParameters?: ListQuery): Promise<{ rows: Name[]; pagination: PaginationModel }> {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const masterFields = `fields[master]=mstDisplayname,site`;
        const siteFields = `fields[site]=siteName`;
        const include = `include=site`;
        const url = `master?${masterFields}&${siteFields}&${include}${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }

            new Deserializer().deserialize(
                json,
                (err: any, apiEnrolments: any[]) => {
                    if (err) {
                        return reject({
                            status: response.status,
                            message: genericError,
                        });
                    }

                    const enrolments: Name[] = apiEnrolments.map(
                        (enrolment) => {
                            return {
                                label: enrolment["mst-displayname"],
                                value: enrolment["id"],
                            };
                        }
                    );

                    return resolve({
                        rows: enrolments,
                        pagination: newPagination,
                    });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export async function getEnrolmentsExcluding(
    translate:Translator,
    masterId: string,
    listParameters?: ListQuery
): Promise<EnrolmentWrapper> {
    try {
        let filter: string = getFilterClause(
            listParameters?.filters,
            listParameters?.searchTerm
        );
        filter =
            filter === ""
                ? `&filter=id!=${masterId}`
                : `${filter};id!=${masterId}`;
        const partialURL = createPartialURL(
            listParameters?.goToPage,
            listParameters?.pageSize,
            listParameters?.sortBy,
            filter
        );
        // Keep this payload intentionally lean for assign-relationship modal use.
        // It needs enrolment identity/display fields and lookup relationships used by columns/filters.
        let masterFields = "fields[master]=mstDisplayname,mstTitle,mstFirstname,mstLastname,mstMiddlename,";
        masterFields += "mstSuffix,mstIdnumber,mstEmailAddress,mstGender,mstCurrent,mstMobileAccessIdentifier,";
        masterFields += "totalTags,totalAccessGroups,totalOssCredentials,site,department,company,profile,masterType";
        const includes = "include=company,site,profile,department,masterType";
        const companyFields = "fields[company]=compName";
        const siteFields = "fields[site]=siteName";
        const profileFields = "fields[profile]=trnName";
        const departmentFields = "fields[department]=deptName";
        const masterTypeFields = "fields[masterType]=trnName";
        const url = `master?${masterFields}&${includes}&${companyFields}&${siteFields}&${profileFields}&${departmentFields}&${masterTypeFields}&${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }

            new Deserializer().deserialize(json, (err: any, master: any[]) => {
                if (err) {
                    return reject({
                        status: response.status,
                        message: genericError,
                    });
                }

                const enrolments: Enrolment[] = master.map((m): Enrolment => {
                    return buildEnrolment(m, undefined, translate);
                });

                return resolve({ rows: enrolments, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export async function getEnrolments(translate:Translator, listParameters?: ListQuery): Promise<EnrolmentWrapper> {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        let masterFields = "fields[master]=mstDisplayname,mstIdnumber,site,department,";
        masterFields += "company,profile,totalTags,mstEmailAddress,mstFirstname,mstLastname,mstMiddlename,masterType,";
        masterFields += "mstGender,mstSuffix,mstCurrent,totalAccessGroups,mstMobileAccessIdentifier,mstTitle,masterDetails,totalOssCredentials"
        const includes = "include=company,site,profile,department,masterType,masterDetails.masterDetailType.settingType";
        const masterDetailsFields = "fields[masterDetail]=masterDetailType,mdValBinary,mdValString";
        const masterDetailTypeFields = "fields[masterDetailType]=settingType";
        const settingTypeFields = "fields[settingType]=stSysname";
        const companyFields = "fields[company]=compName";
        const siteFields = "fields[site]=siteName";
        const profileFields = "fields[profile]=trnName";
        const departmentFields = "fields[department]=deptName";
        const masterTypeFields = "fields[masterType]=trnName";
        const url = `master?${masterFields}&${includes}&${companyFields}&${siteFields}&${profileFields}&${departmentFields}&${masterTypeFields}&${partialURL}&${masterDetailsFields}&${masterDetailTypeFields}&${settingTypeFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }

            new Deserializer().deserialize(json, (err: any, masters: any[]) => {
                if (err) {
                    return reject({
                        status: response.status,
                        message: genericError,
                    });
                }

                const enrolments: Enrolment[] = masters.map((master): Enrolment => {
                    return buildEnrolment(master, "", translate);
                });
                return resolve({ rows: enrolments, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const createApiCompany = (companyId) => {
    return {
        data: {
            type: "company",
            id: companyId,
        },
    };
};

const createApiDepartment = (departmentId) => {
    return {
        data: {
            type: "department",
            id: departmentId,
        },
    };
};

export async function storePerson(
    person: Person,
    createPerson = true
): Promise<any> {
    try {
        const profileId =
            person.profileId && person.profileId > 0 ? person.profileId : 16;
        const newRecord = {
            data: {
                type: "master",
                id: person.id,
                attributes: {
                    mstBlacklist: 0,
                    mstCurrent: person.enableCredentials,
                    mstDescription: person.description,
                    mstDisplayname: person.name,
                    mstEmailAddress: person.email ? person.email : null,
                    mstExpiryDate: 0,
                    mstExpiryEnd: null,
                    mstExpiryStart: null,
                    mstExpiryTime: null,
                    mstExtendedAccess: 0,
                    mstFirstname: person.firstName,
                    mstGender: person.gender,
                    mstHosted: 0,
                    mstIdnumber: person.uid,
                    mstLastname: person.lastName,
                    mstMiddlename: person.middleName,
                    mstMobileAccessIdentifier: person.mstMobileAccessIdentifier
                        ? person.mstMobileAccessIdentifier
                        : null,
                    mstMobileAddress: person.mobileNumber,
                    mstNoapb: person.apbOverride,
                    mstPin: person.pin ? person.pin : null,
                    mstReport: 1,
                    mstSearchTerm: null,
                    mstStartDate: 0,
                    mstStartTime: null,
                    mstSuffix: person.suffix,
                    mstSuspend: 0,
                    mstTitle: person.title,
                    mstType: 1,
                    version: 1,
                },
                relationships: {
                    profile: {
                        data: {
                            type: "profile",
                            id: profileId,
                        },
                    },
                    masterType: {
                        data: {
                            type: "masterType",
                            id: person.timeAndAttendance ? 2 : 1,
                        },
                    },
                    ...(person.siteId && {
                        site: {
                            data: {
                                type: "site",
                                id: person.siteId,
                            },
                        },
                    }),
                },
            },
        };

        newRecord.data.relationships["company"] = person.company
            ? createApiCompany(person.company)
            : {};
        newRecord.data.relationships["department"] = person.department
            ? createApiDepartment(person.department)
            : {};

        const url = "master" + (createPerson ? "" : "/" + newRecord.data.id);
        let response;

        response = await apiConnector.sendData(url, createPerson, newRecord);

        return buildStorePersonResponse(person, createPerson, response);
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const saveCustomDetails = async (person: Person) => {
    try {
        const masterDetailUrl = "masterDetail";
        const masterDetailPromises = Object.values(person.custom ?? {})
            .filter((value) => {
                const customDetail = value as CustomDetail;
                return !!customDetail.id || !!customDetail.value;
            })
            .map((value) => {
                const customDetail = value as CustomDetail;
                const newLocal = {
                    type: "masterDetail",
                    id: customDetail.id > 0 ? customDetail.id : 0,
                    attributes: {
                        mdValBinary: customDetail.isBoolean
                            ? customDetail.value
                            : null,
                        mdValString: customDetail.isBoolean
                            ? null
                            : customDetail.value,
                    },
                    relationships: {
                        master: {
                            data: {
                                type: "master",
                                id: person.id,
                            },
                        },
                        masterDetailType: {
                            data: {
                                type: "masterDetailType",
                                id: customDetail.detailTypeId,
                            },
                        },
                    },
                };
                const masterDetail = {
                    data: customDetail.id > 0 ? newLocal : [newLocal],
                };

                const updatedUrl =
                    customDetail.id > 0
                        ? `${masterDetailUrl}/${customDetail.id}`
                        : masterDetailUrl;

                return apiConnector.sendData(
                    updatedUrl,
                    !(customDetail.id > 0),
                    masterDetail
                );
            });
        if (masterDetailPromises) {
            const masterDetailResponses = await Promise.all(
                masterDetailPromises
            );
            console.debug(
                `Master detail records updated: ${masterDetailResponses.length}`
            );
        }
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};
const buildStorePersonResponse = (
    person: Person,
    isNewPerson: boolean,
    response: Response
) => {
    return new Promise(async (resolve, reject) => {
        if (response) {
            if (isNewPerson) {
                const content = await response.json();

                if (content.errors) {
                    return reject({
                        status: response.status,
                        message: getConstraintViolation(content.errors[0].detail),
                    });
                }
                new Deserializer().deserialize(
                    content,
                    (err: any, master: any) => {
                        if (err) {
                            debug("getEnrolments", err);

                            return reject({
                                status: response.status,
                                id: person.id,
                                success: false,
                                message: genericError,
                            });
                        }
                        person.id = master.id;
                        saveCustomDetails(person);
                        return resolve({ id: master.id, success: true });
                    }
                );

                return;
            }
            saveCustomDetails(person);
            if (response.status === NO_CONTENT) {
                return resolve({
                    id: person.id,
                    master: person,
                    success: true,
                });
            }

            const json = await response.json();
            return reject({
                status: response.status,
                id: person.id,
                success: false,
                message: getConstraintViolation(json.errors[0].detail),
            });
        }

        return reject({
            status: INTERNAL_SERVER_ERROR,
            id: person.id,
            success: false,
            message: genericError,
        });
    });
};

export const deleteImage = async (
    id: string
): Promise<{ id: string; success: boolean }> => {
    try {
        const url = `masterImage/${id}`;
        const masterImage = {
            data: {
                type: "masterImage",
                id: id,
            },
        };
        const response = await apiConnector.deleteData(url, masterImage);

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true });
            }

            const json = await response.json();
            return reject({
                status: response.status,
                id: id,
                success: false,
                message: getConstraintViolation(json.errors[0].detail),
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const deleteEnrolment = async (
    id: string
): Promise<{ id: string; success: boolean }> => {
    try {
        const url = `master/${id}`;
        const enrolment = {
            data: {
                type: "master",
                id: id,
            },
        };
        const response = await apiConnector.deleteData(url, enrolment);

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true });
            }

            const json = await response.json();
            return reject({
                status: response.status,
                id: id,
                success: false,
                message: getConstraintViolation(json.errors[0].detail),
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getMasterImages = async (masterId: string, listParameters?: ListQuery): Promise<string[]> => {
    try {
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filter = (filter === '' ? `&filter=master.id==${masterId}` : `${filter};master.id==${masterId}`);
        const url = "masterImage?" + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const images: string[] = data.map((image) => {
                    return `data:image/png;base64,${image["base64-thumb"]}`;
                });

                return resolve(images);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getMasterForAccessProfiles = async (translate:Translator, accessProfileId: string | number, listParameters?: ListQuery): Promise<EnrolmentWrapper> => {
    try {
        const filtersWithAccessProfile: Filter[] = appendFilter(accessProfileId, "Access Profile", "accessProfile.id", listParameters?.filters ?? []);
        const filter: string = getFilterClause(filtersWithAccessProfile, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const masterFields = "fields[master]=department,company,site,profile&fields[master]=mstTitle,mstLastname,mstFirstname,mstMiddlename,mstDisplayname,mstIdnumber,totalCredentials,totalAccessGroups,mstEmailAddress,mstGender,mstSuffix,mstCurrent,site,company,department,profile,masterType";
        const companyFields = "fields[company]=compName";
        const departmentFields = "fields[department]=deptName";
        const siteFields = "fields[site]=siteName";
        const profileFields = "fields[profile]=trnName";
        const masterTypeFields = "fields[masterType]=trnName";
        const includes = "include=master.site,master.profile,master.company,master.department,master.masterType";
        const url = `masterGroupProfile?${includes}&${masterFields}&${companyFields}&${departmentFields}&${siteFields}&${profileFields}&${masterTypeFields}&${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta)

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            const masters: any[] = json.included?.filter(include => include.type === "master");

            const data = {
                data: masters ?? [],
                included: json.included ?? [],
                meta: json.meta
            }

            if (json.data.length === 0) {
                return resolve({ rows: [], pagination });
            }

            new Deserializer().deserialize(data, (err: any, enrolments: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError })
                }

                const proccessEnrolments: Enrolment[] = enrolments.map(enrolment => buildEnrolment(enrolment, undefined, translate))
                return resolve({ rows: proccessEnrolments, pagination });
            })
        })

    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getMastersForExpiryReason = async (translate: Translator, expiryReasonId: string | number, listParameters?: ListQuery): Promise<EnrolmentWrapper> => {
    try {
        const filtersWithExpiryReason: Filter[] = appendFilter(expiryReasonId, "Expiry Reason", "expiryReason.id", listParameters?.filters ?? []);
        let filters: string = getFilterClause(filtersWithExpiryReason, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const masterExpiryFields = 'fields[masterExpiry]=master,meStartDateTime,meEndDateTime';
        const companyFields = "fields[company]=compName";
        const departmentFields = "fields[department]=deptName";
        const siteFields = "fields[site]=siteName";
        const profileFields = "fields[profile]=trnName";
        const masterTypeFields = "fields[masterType]=trnName";
        const masterFields ="fields[master]=department,company,site,profile&fields[master]=mstTitle,mstLastname,mstFirstname,mstMiddlename,mstDisplayname,mstIdnumber,totalCredentials,totalAccessGroups,mstEmailAddress,mstGender,mstSuffix,mstCurrent,site,company,department,profile,masterType";
        const includes = "include=master.site,master.profile,master.company,master.department,master.masterType";
        const url = `masterExpiry?${includes}&${masterExpiryFields}&${masterFields}&${companyFields}&${siteFields}&${departmentFields}&${profileFields}&${masterTypeFields}&${partialURL}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            const masters: any[] = json.included?.filter(include => include.type === "master");
            const data = { data: masters ?? [], included: json.included ?? [], meta: json.meta }

            if (json.data.length === 0) {
                return resolve({ rows: [], pagination });
            } 

            new Deserializer().deserialize(data, (error: any, enrolments: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const processedEnrolments = enrolments.map((enrolment, index) => ({
                    ...buildEnrolment(enrolment, undefined, translate),
                    masterId: enrolment.id,
                    id: json.data[index].id
                }));
                return resolve({ rows: processedEnrolments, pagination });
            });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

const getGenderLabel = (gender) => {
    if (gender === 'F') {
        return 'Female';
        // return getTranslation('Female', 'female', translate);
    }
    else if (gender === 'M') {
        return 'Male';
        //getTranslation('Male', 'male', translate);
    }
    else {
        return 'Unspecified';
        //getTranslation('Unspecified', 'unspecified', translate);
    }
}

export const getAccessType = (accessType) => {
    if (accessType && accessType.id === '2') {
        return '1';
    }
    return '0';
}

